import { type FC, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';
import { Space, Text, Button } from '@g2u/ui-kit';
import { Badge, FormInstance, Input, Spin } from 'antd';
import cx from 'classnames';
import { EditOutlined } from '@ant-design/icons';

import { AddressType, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { getCheckLostJobs, getSuburb, getSuburbAvailability, putSearchHistory } from 'queries';
import { AddressLookup, CustomerUpdateModal, ErrorBox, Section } from 'components';
import { useText } from 'helpers/hooks/useText';
import { Toast, parseErrors } from 'helpers';
import { BookingStore } from 'store';
import styles from '../location.module.scss';

type Props = {
  form: FormInstance;
} & WithOptionalDataTest;

export const ServiceLocation: FC<Props> = observer((props: Props) => {
  const { form } = props;
  const { customer, location, setLocation, isNewCustomer, lostJob, setLostJob, callback } = BookingStore;
  const [checkAvailability, setCheckAvailability] = useState<boolean>(!!lostJob?.searchHistoryID);
  const [checkedSuburb, setCheckedSuburb] = useState<string>(customer?.address.suburb || location.suburb);
  const [checkedPostcode, setCheckedPostcode] = useState<string>(customer?.address.postcode || location.postcode);
  const [suburbLookupError, setSuburbLookupError] = useState<JsonDataItem | null>();
  const addressValue = `${checkedSuburb}, ${checkedPostcode}, ${customer?.address.state || location.state}`;
  const { getText } = useText();
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [inputPlaceholder, setInputPlaceholder] = useState<string>(
    checkedSuburb ? addressValue : 'Enter Suburb and Postal Code',
  );
  const updatedProfile = useRef<JsonDataItem>(undefined);

  // Suburb lookup: get list
  const { mutate: suburbLookup, data: suggestedSuburbs } = useMutation(getSuburb, {
    onSuccess: (data: JsonDataItem) => {
      data && data.length > 0
        ? setSuburbLookupError(null)
        : setSuburbLookupError({ message: 'No matching suburb found. Please select a different suburb' });
    },
    onError: (error: JsonDataItem) => {
      setSuburbLookupError(error);
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  useQuery(['check-lost-jobs'], getCheckLostJobs, {
    enabled: !lostJob?.searchHistoryID,
    onSuccess: (data) => {
      setCheckAvailability(!data.forceLostJob);
      setLostJob(data);
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  // Automatically check availability when address changed
  const { mutate: updateSearchHistory } = useMutation(putSearchHistory, {
    onSuccess: () => {
      setCheckedSuburb(updatedProfile?.current?.address.suburb);
      setCheckedPostcode(updatedProfile?.current?.address.postcode);
    },
    onError: (data: JsonDataItem) => {
      Toast.show({ description: parseErrors(data).join(', '), type: Toast.Type.Error });
    },
  });

  const { data: suburbAvailability, isInitialLoading: isAvailabilityLoading } = useQuery(
    [
      'get-availability',
      {
        suburb: checkedSuburb,
        postcode: checkedPostcode,
        customerId: customer?.ID,
      },
    ],
    getSuburbAvailability,
    {
      enabled: !!checkedSuburb && !!checkedPostcode && checkAvailability,
      onSuccess: (data) => {
        setLostJob({ ...lostJob, searchHistoryID: data.searchHistoryID });
      },
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    },
  );

  useEffect(() => {
    const { postcode, suburb, state } = callback?.record || {};
    // check suburb availability if complete location data provided in the callback
    if (postcode && suburb && state) {
      setCheckedSuburb(postcode);
      setCheckedPostcode(suburb);
      form.setFieldValue('serviceLocationSuburb', `${suburb}, ${postcode}, ${state}`);
      // update location to use on the payment page
      setLocation({ postcode, suburb, state });
    }
  }, []);

  return (
    <Section title="Service Location">
      <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Large}>
        {isNewCustomer && (
          <Text.$ className="script-box" mark>
            {getText('booking.location.requestPostcodeSuburb')}
          </Text.$>
        )}
        <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
          <Space.$ compact fullWidth direction={Space.Direction.Horizontal} size={Space.Size.ExtraSmall}>
            {isNewCustomer ? (
              <AddressLookup
                name="serviceLocationSuburb"
                placeholder={'Enter Suburb and Postal Code'}
                onAddressSearch={(value: string) => {
                  suburbLookup({ value, type: AddressType.Physical });
                }}
                onAddressSelect={(option: JsonDataItem) => {
                  setCheckedSuburb(option.data.suburb);
                  setCheckedPostcode(option.data.postcode);
                  isNewCustomer && setLocation(option.data);
                }}
                initialValue={checkedSuburb ? addressValue : undefined}
                label=" "
                required
                addressOptions={suggestedSuburbs?.map((item: JsonDataItem) => {
                  return {
                    label: `${item.suburb}, ${item.postcode}, ${item.state}`,
                    value: `${item.suburb}, ${item.postcode}, ${item.state}`,
                    data: item,
                  };
                })}
                className={cx(styles.location__suburb, styles.location__formItem, styles.location__formItem_horizontal)}
                dataTestId="home-suburb"
              />
            ) : (
              <>
                <Input
                  disabled
                  placeholder={inputPlaceholder}
                  className={cx(
                    styles.location__suburb,
                    styles.location__formItem,
                    styles.location__formItem_horizontal,
                  )}
                />
                <Button.$
                  dataTestId="edit-customer"
                  onClick={() => {
                    setShowUpdateModal(true);
                  }}
                >
                  <EditOutlined />
                </Button.$>
              </>
            )}
          </Space.$>
          <CustomerUpdateModal
            modalTitle="Update Customer File Details"
            okText="Update"
            isOpen={showUpdateModal}
            onOk={(data: JsonDataItem | null) => {
              if (data) {
                const updatedAddress = `${data.address.suburb}, ${data.address.postcode}`;
                form.setFieldValue('serviceLocationSuburb', updatedAddress);
                setInputPlaceholder(`${updatedAddress}, ${data.address.state}`); // Update placeholder on modal save
                updatedProfile.current = data;
                updateSearchHistory({
                  searchId: lostJob?.searchHistoryID,
                  customerId: customer?.ID,
                });
                setShowUpdateModal(false);
              }
            }}
            onCancel={() => {
              setShowUpdateModal(false);
            }}
          />
          {suburbLookupError && <ErrorBox description="Try looking up suburb again" error={suburbLookupError} />}
          {isAvailabilityLoading && <Spin />}
          {suburbAvailability && (
            <Space.$ direction={Space.Direction.Vertical}>
              <Badge
                status={suburbAvailability?.available ? 'success' : 'warning'}
                text={
                  <>
                    On-site services are {suburbAvailability?.available ? '' : <b>Not</b>} available with <b>Local</b>{' '}
                    technicians {suburbAvailability?.available ? '' : 'in the next 7 days'}
                  </>
                }
              />
              <Badge
                status={suburbAvailability?.available ? 'success' : 'warning'}
                text={
                  suburbAvailability?.available
                    ? 'Remote services are available'
                    : 'Remote technicians are available and surrounding technicians might be available'
                }
              />
            </Space.$>
          )}
        </Space.$>
      </Space.$>
    </Section>
  );
});
